import {IShape} from 'common/src/modules/movie/movieShapes.types';
import {Timestamped} from 'common/src/modules/movie/movieRendering';
import {MovieShapeLayerType} from 'common/src/modules/movie/movie.enums';
import BezierCurve from 'common/src/modules/movie/utils/bezierCurve';
import getShapeTransformMatrix from './getShapeTransformMatrix';

function getShapeBoundingBox(shape: Timestamped<IShape>): {
  x: number;
  y: number;
  w: number;
  h: number;
} {
  function getBaseBoundaries() {
    switch (shape.ty) {
      case MovieShapeLayerType.Rectangle:
      case MovieShapeLayerType.Ellipse:
        return {
          x: shape.p[0] - shape.s[0] / 2,
          y: shape.p[1] - shape.s[1] / 2,
          w: shape.s[0],
          h: shape.s[1],
        };
      case MovieShapeLayerType.Polystar:
        return {
          x: shape.p[0] - shape.oRad,
          y: shape.p[1] - shape.oRad,
          w: shape.oRad * 2,
          h: shape.oRad * 2,
        };
      case MovieShapeLayerType.Path:
        const beziers = BezierCurve.fromPath(shape.path);
        const xEdges = beziers.map((bezier) => bezier.getBoundaries(0)).flat();
        const yEdges = beziers.map((bezier) => bezier.getBoundaries(1)).flat();
        const x = Math.min(...xEdges),
          y = Math.min(...yEdges);
        const w = Math.max(...xEdges) - x,
          h = Math.max(...yEdges) - y;

        return {x, y, w, h};
    }
  }

  const {x, y, w, h} = getBaseBoundaries();

  const transformMatrix = getShapeTransformMatrix(shape);

  const [topLeft, topRight, bottomLeft, bottomRight] = [
    [y, x],
    [y, x + w],
    [y + h, x],
    [y + h, x + w],
  ].map(([y, x]) => {
    return new DOMPoint(x, y).matrixTransform(transformMatrix);
  });

  const X = [topLeft.x, topRight.x, bottomLeft.x, bottomRight.x];
  const Y = [topLeft.y, topRight.y, bottomLeft.y, bottomRight.y];

  const left = Math.min(...X),
    right = Math.max(...X),
    top = Math.min(...Y),
    bottom = Math.max(...Y);

  return {
    x: left,
    y: top,
    w: right - left,
    h: bottom - top,
  };
}

export default getShapeBoundingBox;
